import React, { useState } from "react";
import {
  Box,
  Text,
  TextArea,
  Select,
  Button,
  Notification,
  Heading,
  Layer,
} from "grommet";
import { Add } from "grommet-icons";
import { useNavigate, useParams } from "react-router-dom";
import { HeaderNavigation } from "../../components/Headers/AdminHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import BreadcrumbNavigation from "../../components/AdminPortal/BreadcrumbNavigation";
import AddCourseModal from "../../components/AdminPortal/AddCourseModal"; // adjust the import path as needed

const AddLearningPath = () => {
  const navigate = useNavigate();
  const { portfolio_id, skillId } = useParams();
  const [notification, setNotification] = useState(null);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);

  // Options for the mapped learning path/course dropdown
  const defaultLearningOption = {
    label: "Select Mapped Learning Path & Course",
    value: "",
    disabled: true,
  };
  const courseOptions = [
    defaultLearningOption,
    {
      label: "HPE Gen11 - Server Management",
      value: "HPE Gen11 - Server Management",
    },
    { label: "HPE Compute Ops Manager", value: "HPE Compute Ops Manager" },
  ];

  // Form state for the Learning Path page
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [learningPath, setLearningPath] = useState(defaultLearningOption.value);

  // Validate that required fields are not empty
  const isFormValid =
    questionText.trim() !== "" &&
    questionType.trim() !== "" &&
    learningPath.trim() !== "";

  const handleCancel = () => {
    navigate(-1);
  };

  const handleAddNewCourse = () => {
    setShowAddCourseModal(true);
  };

  const handleSubmit = (actionType) => {
    setNotification({
      message: "Question submitted successfully!",
      status: "normal",
    });

    if (actionType === "submitAndAdd") {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      setTimeout(() => {
        navigate(`/admin/portfolio/${portfolio_id}/skill-question/${skillId}`);
      }, 1000);
    }
  };

  return (
    <Box fill direction="column">
      {/* Global Header */}
      <HeaderNavigation />

      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation
        breadcrumbs={[
          {
            label: "Manage Learning Paths",
            link: `/admin/portfolio/${portfolio_id}/learning-paths`,
          },
          {
            label: "Add Learning Path",
            link: `/admin/portfolio/${portfolio_id}/add-learning-path`,
          },
        ]}
      />

      {/* Brand Line Under Breadcrumb */}
      <Box
        height="2px"
        background="brand"
        margin={{ vertical: "small" }}
        width="100%"
      />

      {/* Main Content Area */}
      <Box
        flex
        overflow="auto"
        align="center"
        justify="center"
        pad="small"
        gap="medium"
      >
        <Heading level="1" weight="bold" alignSelf="center" align="center">
          Add Learning Path
        </Heading>
        <Box
          width="large"
          pad="medium"
          round="small"
          elevation="small"
          background="light-1"
        >
          {/* Question Field */}
          <Box margin={{ bottom: "small" }}>
            <Box
              direction="row"
              gap="xxsmall"
              align="center"
              margin={{ bottom: "xxsmall" }}
            >
              <Text>Question</Text>
              <Text color="red">*</Text>
            </Box>
            <TextArea
              placeholder="Enter your question here..."
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              resize="vertical"
            />
          </Box>

          {/* Question Type Dropdown */}
          <Box margin={{ bottom: "small" }}>
            <Box
              direction="row"
              gap="xxsmall"
              align="center"
              margin={{ bottom: "xxsmall" }}
            >
              <Text>Question Type</Text>
              <Text color="red">*</Text>
            </Box>
            <Select
              placeholder="Select question type"
              options={["Y/N", "Range"]}
              value={questionType}
              onChange={({ option }) => setQuestionType(option)}
            />
          </Box>

          {/* Learning Path - Course Dropdown */}
          <Box margin={{ bottom: "small" }}>
            <Text margin={{ bottom: "xxsmall" }}>Learning Path - Course</Text>
            <Select
              options={courseOptions}
              value={
                courseOptions.find((opt) => opt.value === learningPath)
                  ?.label || defaultLearningOption.label
              }
              onChange={({ option }) => {
                if (!option.disabled) {
                  setLearningPath(option.value);
                }
              }}
            />
          </Box>

          {/* OR and Add New Course Button */}
          <Box
            direction="row"
            align="center"
            gap="small"
            margin={{ vertical: "small" }}
          >
            <Text>OR</Text>
            <Button
              icon={<Add />}
              label="Add New Course"
              onClick={handleAddNewCourse}
              secondary
              color="brand"
              style={{ width: "auto" }}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            direction="row"
            gap="small"
            margin={{ top: "medium" }}
            justify="end"
          >
            <Button label="Cancel" onClick={handleCancel} />
            <Button
              label="Submit & Add Question"
              onClick={() => handleSubmit("submitAndAdd")}
              disabled={!isFormValid}
              secondary
            />
            <Button
              label="Submit & Close"
              primary
              onClick={() => handleSubmit("submitAndClose")}
              disabled={!isFormValid}
            />
          </Box>
        </Box>
      </Box>

      {/* Modal for Adding a New Course */}
      {showAddCourseModal && (
        <Layer
          onEsc={() => setShowAddCourseModal(false)}
          onClickOutside={() => setShowAddCourseModal(false)}
          responsive={false}
          animation="fadeIn"
        >
          <AddCourseModal
            onClose={() => setShowAddCourseModal(false)}
            portfolio_id={portfolio_id}
          />
        </Layer>
      )}

      {/* Sticky Footer */}
      <StickyFooter />

      {/* Toast Notification */}
      {notification && (
        <Notification
          toast
          message={notification.message}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
    </Box>
  );
};

export default AddLearningPath;
